/**
 * "List on eBay" modal (eBay step 5 MVP) — opened from the lot drawer for
 * card lots. Prefills title/price/photos/aspects from the lot, pulls the
 * seller's business policies + locations + a category suggestion from the
 * ebay-listing Edge Function, and publishes inventory-item -> offer -> listing.
 * eBay rejections are shown VERBATIM so category/aspect/condition rules can
 * be iterated live. Slabs get condition GRADED, raw cards UNGRADED.
 */
import { DB } from "../lib/db";
import { sb } from "../lib/supabase";
import { escapeHtml, toast } from "../lib/dom";
import { openModal, closeModal } from "./modal";
import { render } from "../state";
import { lotPhotos, lotPhotoUrl } from "../lib/lotPhotos";

interface PrepareData {
  environment: string;
  fulfillmentPolicies: Array<{ id: string; name: string }>;
  paymentPolicies: Array<{ id: string; name: string }>;
  returnPolicies: Array<{ id: string; name: string }>;
  locations: Array<{ key: string; name: string }>;
  suggestions: Array<{ categoryId: string; name: string; path: string }>;
}

let _st: { kind: "card" | "sealed"; lotId: string; row: any; publishedListingId?: string } | null = null;

const EBAY_GAME_NAMES: Record<string, string> = {
  "Pokemon": "Pokémon TCG",
  "Magic: The Gathering": "Magic: The Gathering",
  "Yu-Gi-Oh": "YuGiOh TCG",
  "One Piece": "One Piece Card Game",
  "Lorcana": "Disney Lorcana TCG",
};

function v(id: string): string {
  return (document.getElementById(id) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null)?.value?.trim() || "";
}

export function openEbayListingModal(lotId: string, kind: "card" | "sealed" = "card"): void {
  const table = kind === "card" ? "cards" : "sealed";
  const keyField = kind === "card" ? "Card Lot ID" : "Lot ID";
  const row = DB.table(table).find(x => x[keyField] === lotId);
  if (!row) { toast("Lot not found", "err"); return; }
  _st = { kind, lotId, row };

  const qty = Number(row["Qty"]) || 1;
  const unitFMV = qty > 0 ? +((Number(row["Current FMV"]) || 0) / qty).toFixed(2) : 0;
  const isSlab = kind === "card" && String(row["Inventory Class"] || "") === "Slab";
  const condGrade = kind === "card" ? String(row["Condition/Grade"] || "") : "";
  const gradeMatch = condGrade.match(/^([A-Za-z]+)\s+([\d.]+|Authentic|Pending)/);
  const grader = isSlab ? (gradeMatch?.[1] || "") : "";
  const grade = isSlab ? (gradeMatch?.[2] || "") : "";
  const name = kind === "card" ? String(row["Card Name"] || "") : String(row["Product"] || "");
  const set = String(row["Set"] || "");
  const num = kind === "card" ? String(row["Card #"] || "") : "";
  const game = String(row["Game"] || "Pokemon");
  const productType = kind === "sealed" ? String(row["Product Type"] || "") : "";

  const gameLabel = game === "Pokemon" ? "Pokemon" : game;
  const title = (kind === "sealed"
    ? [
        name,
        set && !name.toLowerCase().includes(set.toLowerCase()) ? set : "",
        gameLabel, "Sealed",
        productType && !name.toLowerCase().includes(productType.toLowerCase()) ? productType : "",
      ]
    : [
        isSlab && grader ? `${grader} ${grade}` : "",
        name, num ? `#${num}` : "", set, gameLabel, "Card",
      ]
  ).filter(Boolean).join(" ").slice(0, 80);

  const photos = lotPhotos(row);
  const stockImg = String(row["Image URL"] || "");
  const photoChecks = photos.map((p) => `
    <label class="inline-flex flex-col items-center gap-1 cursor-pointer">
      <img src="${escapeHtml(lotPhotoUrl(p))}" class="rounded border border-[var(--border)]" style="height:64px" alt="">
      <input type="checkbox" data-ebl-photo="${escapeHtml(lotPhotoUrl(p))}" checked>
    </label>`).join("");
  const stockCheck = stockImg ? `
    <label class="inline-flex flex-col items-center gap-1 cursor-pointer" title="Catalog stock image">
      <img src="${escapeHtml(stockImg)}" class="rounded border border-[var(--border)] opacity-70" style="height:64px" alt="">
      <span class="text-[10px] text-[var(--muted)]">stock<input type="checkbox" data-ebl-photo="${escapeHtml(stockImg)}" ${photos.length ? "" : "checked"} class="ml-1"></span>
    </label>` : "";

  const body = `
    <div class="space-y-3" id="eblForm">
      <div id="eblSetup" class="panel-2 p-3">
        <h4 class="font-semibold text-sm mb-1">Seller setup</h4>
        <div id="eblSetupStatus" class="text-xs text-[var(--muted)]">Checking your eBay policies & locations…</div>
        <div id="eblBootstrap" class="hidden mt-2">
          <div class="text-xs text-amber-300 mb-2">Your eBay account is missing shipping/payment/return policies or an inventory location. Create simple defaults (editable later on eBay):</div>
          <div class="grid grid-cols-3 gap-2 mb-2">
            <input class="input" id="ebl_zip" placeholder="ZIP">
            <input class="input" id="ebl_city" placeholder="City">
            <input class="input" id="ebl_state" placeholder="State (GA)">
          </div>
          <button class="btn btn-ghost !py-1 !px-3 text-xs" onclick="_eblBootstrap()">Create defaults on eBay</button>
        </div>
      </div>

      <div class="grid grid-cols-12 gap-2">
        <div class="col-span-12">
          <label class="label text-xs">Title (max 80)</label>
          <input class="input" id="ebl_title" maxlength="80" value="${escapeHtml(title)}">
        </div>
        <div class="col-span-4 sm:col-span-2">
          <label class="label text-xs" id="ebl_priceLabel">Price ($)</label>
          <input class="input" id="ebl_price" type="number" step="0.01" value="${unitFMV || ""}">
        </div>
        <div class="col-span-4 sm:col-span-2">
          <label class="label text-xs">Qty</label>
          <input class="input" id="ebl_qty" type="number" step="1" min="1" max="${qty}" value="1">
        </div>
        <div class="col-span-4 sm:col-span-3">
          <label class="label text-xs">Condition</label>
          <select class="select" id="ebl_cond">
            ${kind === "sealed"
              ? `<option value="NEW" selected>New (factory sealed)</option>`
              : `<option value="GRADED" ${isSlab ? "selected" : ""}>Graded</option>
                 <option value="UNGRADED" ${isSlab ? "" : "selected"}>Ungraded</option>`}
          </select>
        </div>
        <div class="col-span-12 sm:col-span-5">
          <label class="label text-xs">Category ID <span class="text-[var(--muted)]">(suggestions load below)</span></label>
          <input class="input" id="ebl_cat" placeholder="e.g. 183454">
        </div>
        <div class="col-span-6 sm:col-span-3">
          <label class="label text-xs">Format</label>
          <select class="select" id="ebl_format" onchange="_eblFormatChange()">
            <option value="FIXED_PRICE">Buy It Now (fixed price)</option>
            <option value="AUCTION">Auction</option>
          </select>
        </div>
        <div class="col-span-6 sm:col-span-3 hidden" id="eblDurWrap">
          <label class="label text-xs">Auction duration</label>
          <select class="select" id="ebl_dur">
            <option value="DAYS_7" selected>7 days</option>
            <option value="DAYS_3">3 days</option>
            <option value="DAYS_5">5 days</option>
            <option value="DAYS_10">10 days</option>
          </select>
        </div>
        <div class="col-span-6 sm:col-span-3">
          <label class="label text-xs">Start</label>
          <select class="select" id="ebl_start" onchange="_eblStartChange()">
            <option value="now">Immediately</option>
            <option value="sched">Scheduled\u2026</option>
          </select>
        </div>
        <div class="col-span-6 sm:col-span-3 hidden" id="eblStartAtWrap">
          <label class="label text-xs">Start at</label>
          <div class="flex gap-2">
            <input class="input flex-1" id="ebl_startAt" type="datetime-local" onchange="_eblStartAtNote(true)" oninput="_eblStartAtNote(false)">
            <button type="button" class="btn btn-ghost !px-2" onclick="_eblStartAtNote(true)" title="Confirm date/time (closes the picker)">\u2713</button>
          </div>
          <div id="eblStartAtNote" class="text-xs text-[var(--muted)] mt-1"></div>
        </div>
        <div id="eblSuggest" class="col-span-12 text-xs text-[var(--muted)]"></div>
        ${isSlab ? `
        <div class="col-span-4">
          <label class="label text-xs">Grader</label>
          <input class="input" id="ebl_grader" value="${escapeHtml(grader)}">
        </div>
        <div class="col-span-4">
          <label class="label text-xs">Grade</label>
          <input class="input" id="ebl_grade" value="${escapeHtml(grade)}">
        </div>
        <div class="col-span-4">
          <label class="label text-xs">Cert # (optional)</label>
          <input class="input" id="ebl_cert" placeholder="Certification number">
        </div>` : ""}
        <div class="col-span-12 sm:col-span-4">
          <label class="label text-xs">Shipping policy</label>
          <select class="select" id="ebl_ful"></select>
        </div>
        <div class="col-span-12 sm:col-span-4">
          <label class="label text-xs">Payment policy</label>
          <select class="select" id="ebl_pay"></select>
        </div>
        <div class="col-span-12 sm:col-span-4">
          <label class="label text-xs">Return policy</label>
          <select class="select" id="ebl_ret"></select>
        </div>
        <div class="col-span-12 sm:col-span-6">
          <label class="label text-xs">Ship-from location</label>
          <select class="select" id="ebl_loc"></select>
        </div>
        <div class="col-span-12">
          <label class="label text-xs">Description</label>
          <textarea class="input" id="ebl_desc" rows="3">${escapeHtml(`${title}. Sold by Enigma Collective. See photos for exact item and condition.`)}</textarea>
        </div>
        <div class="col-span-12">
          <label class="label text-xs">Photos (eBay needs at least 1 — real item photos strongly preferred${isSlab ? ", REQUIRED for slabs" : ""})</label>
          <div class="flex flex-wrap gap-2">${photoChecks}${stockCheck}
            ${!photos.length ? `<div class="text-xs text-amber-300 self-center">No lot photos yet — add them from the lot drawer (“Your photos”).</div>` : ""}
          </div>
        </div>
      </div>
      <div id="eblResult" class="hidden panel-2 p-3 text-xs" style="white-space:pre-wrap; word-break:break-word;"></div>
    </div>`;
  const footer = `
    <button class="btn btn-ghost" onclick="closeModal()">Cancel</button>
    <button class="btn btn-primary" id="eblPublishBtn" onclick="_eblPublish()">Publish to eBay</button>`;
  openModal({ title: `List on eBay — ${escapeHtml(lotId)}`, body, footer, wide: true });
  void eblPrepare(title);
  void eblDupCheck(lotId, String(row["Status"] || ""), qty);
}

/** Warn when this lot is already listed — a live eBay listing row for it, or
 *  a lot Status of "Listed" (listed elsewhere/manually). Non-blocking, but
 *  publish asks for an explicit confirm when a live listing exists. */
async function eblDupCheck(lotId: string, curStatus: string, qtyOnHand: number): Promise<void> {
  let warn = "";
  try {
    const { data } = await sb.from("ebay_listings")
      .select("listing_id, environment, created_at")
      .eq("lot_id", lotId).eq("status", "published")
      .order("id", { ascending: false }).limit(1);
    const hit = (data || [])[0] as any;
    if (hit?.listing_id) {
      if (_st) _st.publishedListingId = String(hit.listing_id);
      warn = `This lot already has a live eBay listing (<b>${escapeHtml(String(hit.listing_id))}</b>, ${escapeHtml(String(hit.environment))}, ${escapeHtml(new Date(hit.created_at).toLocaleDateString())}).`;
    }
  } catch { /* non-fatal */ }
  if (!warn && curStatus === "Listed") warn = `This lot's status is already <b>Listed</b>.`;
  if (!warn && qtyOnHand <= 0) warn = `This lot has <b>zero quantity on hand</b> (status: ${escapeHtml(curStatus || "?")}) \u2014 there's nothing left to sell.`;
  if (warn) {
    document.getElementById("eblForm")?.insertAdjacentHTML("afterbegin",
      `<div class="panel-2 p-2 text-xs text-amber-300">⚠ ${warn} Publishing again will create a duplicate listing.</div>`);
  }
}

async function eblPrepare(q: string): Promise<void> {
  const status = document.getElementById("eblSetupStatus");
  try {
    const { data, error } = await sb.functions.invoke("ebay-listing", { body: { action: "prepare", q } });
    if (error) throw error;
    if ((data as any)?.error) throw new Error((data as any).error);
    const p = data as PrepareData;
    const fill = (id: string, opts: Array<{ id?: string; key?: string; name: string }>) => {
      const sel = document.getElementById(id) as HTMLSelectElement | null;
      if (!sel) return;
      sel.innerHTML = opts.length
        ? opts.map(o => `<option value="${escapeHtml(o.id || o.key || "")}">${escapeHtml(o.name)}</option>`).join("")
        : `<option value="">(none on account)</option>`;
    };
    fill("ebl_ful", p.fulfillmentPolicies);
    fill("ebl_pay", p.paymentPolicies);
    fill("ebl_ret", p.returnPolicies);
    fill("ebl_loc", p.locations.map(l => ({ id: l.key, name: l.name || l.key })));
    const missing = !p.fulfillmentPolicies.length || !p.paymentPolicies.length || !p.returnPolicies.length || !p.locations.length;
    if (status) status.innerHTML = missing
      ? `<span class="text-amber-300">Setup incomplete</span> · env: <b>${escapeHtml(p.environment)}</b>`
      : `<span class="text-emerald-300">✓ Ready</span> · env: <b>${escapeHtml(p.environment)}</b>`;
    document.getElementById("eblBootstrap")?.classList.toggle("hidden", !missing);
    const sugEl = document.getElementById("eblSuggest");
    if (sugEl) {
      sugEl.innerHTML = p.suggestions.length
        ? "Suggested: " + p.suggestions.map(s =>
            `<button type="button" class="underline text-cyan-300 mr-2" onclick="_eblUseCat('${escapeHtml(s.categoryId)}')" title="${escapeHtml(s.path)}">${escapeHtml(s.name)} (${escapeHtml(s.categoryId)})</button>`).join("")
        : "No category suggestions returned — enter a category ID manually.";
      const first = p.suggestions[0];
      const cat = document.getElementById("ebl_cat") as HTMLInputElement | null;
      if (first && cat && !cat.value) cat.value = first.categoryId;
    }
  } catch (e: any) {
    if (status) status.innerHTML = `<span class="text-rose-300">✕ ${escapeHtml(e?.message || String(e))}</span>`;
  }
}

async function eblBootstrap(): Promise<void> {
  const zip = v("ebl_zip"), city = v("ebl_city"), state = v("ebl_state");
  if (!zip || !city || !state) { toast("ZIP, city, and state are required", "err"); return; }
  toast("Creating defaults on eBay…");
  try {
    const { data, error } = await sb.functions.invoke("ebay-listing", { body: { action: "bootstrap", postalCode: zip, city, state } });
    if (error) throw error;
    const res = (data as any)?.results || {};
    const bad = Object.entries(res).filter(([, r]) => r !== "created");
    if (bad.length) {
      const el = document.getElementById("eblResult");
      if (el) { el.classList.remove("hidden"); el.textContent = "Bootstrap results:\n" + JSON.stringify(res, null, 2); }
      toast("Some defaults could not be created — details below", "err");
    } else {
      toast("Defaults created ✓");
    }
    void eblPrepare(v("ebl_title"));
  } catch (e: any) {
    toast("Bootstrap failed: " + (e?.message || e), "err");
  }
}

async function eblPublish(): Promise<void> {
  if (!_st) return;
  const row = _st.row;
  const imageUrls = Array.from(document.querySelectorAll<HTMLInputElement>("[data-ebl-photo]"))
    .filter(c => c.checked).map(c => c.getAttribute("data-ebl-photo") || "").filter(Boolean);
  const missing: string[] = [];
  if (!v("ebl_title")) missing.push("title");
  if (!Number(v("ebl_price"))) missing.push("price");
  if (!v("ebl_cat")) missing.push("category ID");
  if (!v("ebl_ful") || !v("ebl_pay") || !v("ebl_ret")) missing.push("policies");
  if (!v("ebl_loc")) missing.push("location");
  if (!imageUrls.length) missing.push("at least one photo");
  let startAt = "";
  if (v("ebl_start") === "sched") {
    const raw = v("ebl_startAt");
    if (!raw) missing.push("start date/time");
    else {
      const d = new Date(raw);
      if (isNaN(d.getTime()) || d.getTime() < Date.now() + 60_000) missing.push("a FUTURE start date/time");
      else startAt = d.toISOString();
    }
  }
  if (missing.length) { toast("Missing: " + missing.join(", "), "err"); return; }

  // Duplicate guard (Paul, 2026-07-23): already-listed lots need an explicit ok.
  const curStatus = String(row["Status"] || "");
  const dupReason = _st.publishedListingId
    ? `it already has live eBay listing ${_st.publishedListingId}`
    : (curStatus === "Listed" ? 'its status is already "Listed"'
    : ((Number(row["Qty"]) || 0) <= 0 ? "it has ZERO quantity on hand" : ""));
  if (dupReason && !confirm(`Heads up: ${dupReason}. Publish another listing for this lot anyway?`)) return;

  const game = String(row["Game"] || "Pokemon");
  const isGraded = v("ebl_cond") === "GRADED";
  const aspects: Record<string, string[]> = _st.kind === "sealed"
    ? {
        "Game": [EBAY_GAME_NAMES[game] || game],
        "Set": [String(row["Set"] || "")].filter(Boolean),
        "Language": ["English"],
      }
    : {
        "Game": [EBAY_GAME_NAMES[game] || game],
        "Card Name": [String(row["Card Name"] || "")].filter(Boolean),
        "Set": [String(row["Set"] || "")].filter(Boolean),
        "Card Number": [String(row["Card #"] || "")].filter(Boolean),
        "Language": ["English"],
        "Graded": [isGraded ? "Yes" : "No"],
      };
  if (isGraded && _st.kind === "card") {
    if (v("ebl_grader")) aspects["Professional Grader"] = [v("ebl_grader")];
    if (v("ebl_grade"))  aspects["Grade"] = [v("ebl_grade")];
    if (v("ebl_cert"))   aspects["Certification Number"] = [v("ebl_cert")];
  }
  for (const k of Object.keys(aspects)) if (!aspects[k].length) delete aspects[k];

  const listing = {
    sku: _st.lotId,
    lotKind: _st.kind,
    lotId: _st.lotId,
    title: v("ebl_title"),
    description: v("ebl_desc") || v("ebl_title"),
    price: Number(v("ebl_price")),
    qty: Number(v("ebl_qty")) || 1,
    condition: v("ebl_cond"),
    format: v("ebl_format") || "FIXED_PRICE",
    duration: v("ebl_dur") || "DAYS_7",
    startAt,
    categoryId: v("ebl_cat"),
    locationKey: v("ebl_loc"),
    fulfillmentPolicyId: v("ebl_ful"),
    paymentPolicyId: v("ebl_pay"),
    returnPolicyId: v("ebl_ret"),
    imageUrls,
    aspects,
  };

  const btn = document.getElementById("eblPublishBtn") as HTMLButtonElement | null;
  if (btn) { btn.disabled = true; btn.textContent = "Publishing…"; }
  const out = document.getElementById("eblResult");
  try {
    const { data, error } = await sb.functions.invoke("ebay-listing", { body: { action: "publish", listing } });
    // supabase-js throws FunctionsHttpError on non-2xx; pull the payload out.
    let payload: any = data;
    if (error) {
      try { payload = await (error as any).context?.json?.(); } catch { /* keep null */ }
      if (!payload) throw error;
    }
    if (payload?.ok) {
      toast("Listed on eBay ✓");
      if (out) {
        out.classList.remove("hidden");
        out.innerHTML = `<span class="text-emerald-300">✓ Published!</span> Listing ID <b>${escapeHtml(payload.listingId)}</b> — ` +
          `<a class="text-cyan-300 underline" href="${escapeHtml(payload.viewUrl)}" target="_blank" rel="noopener">view on eBay</a>`;
      }
      // Flip the lot to Listed (only from Available — never stomp machine
      // statuses like Sold/At Grading) and refresh the app behind the modal.
      if (curStatus === "Available") {
        DB.update(_st.kind === "card" ? "cards" : "sealed",
          _st.kind === "card" ? "Card Lot ID" : "Lot ID",
          _st.lotId, { "Status": "Listed" });
        render();
      }
      // The publish button becomes the way OUT — no more Cancel-only exits.
      if (btn) {
        btn.textContent = "Done ✓";
        btn.disabled = false;
        btn.onclick = () => { closeModal(); };
      }
    } else {
      if (out) {
        out.classList.remove("hidden");
        out.textContent = `eBay rejected the listing at step "${payload?.step || "?"}" (HTTP ${payload?.status || "?"}):\n\n` +
          (payload?.body || payload?.error || "Unknown error");
      }
      toast("eBay rejected the listing — details below", "err");
      if (btn) { btn.disabled = false; btn.textContent = "Publish to eBay"; }
    }
  } catch (e: any) {
    if (out) { out.classList.remove("hidden"); out.textContent = "Request failed: " + (e?.message || String(e)); }
    toast("Publish failed — details below", "err");
    if (btn) { btn.disabled = false; btn.textContent = "Publish to eBay"; }
  }
}

function eblFormatChange(): void {
  const isAuction = v("ebl_format") === "AUCTION";
  document.getElementById("eblDurWrap")?.classList.toggle("hidden", !isAuction);
  const lbl = document.getElementById("ebl_priceLabel");
  if (lbl) lbl.textContent = isAuction ? "Starting bid ($)" : "Price ($)";
  const qty = document.getElementById("ebl_qty") as HTMLInputElement | null;
  if (qty && isAuction) qty.value = "1";
  if (qty) qty.disabled = isAuction; // auctions are single-quantity on eBay
}

function eblStartChange(): void {
  document.getElementById("eblStartAtWrap")?.classList.toggle("hidden", v("ebl_start") !== "sched");
  eblStartAtNote(false);
}

/** Live confirmation line under the Start-at picker; done=true also blurs the
 *  input, which closes the browser's native (and lingering) picker panel. */
function eblStartAtNote(done: boolean): void {
  const inp = document.getElementById("ebl_startAt") as HTMLInputElement | null;
  const note = document.getElementById("eblStartAtNote");
  if (!inp || !note) return;
  if (done) inp.blur();
  const raw = inp.value;
  if (!raw) { note.textContent = "Pick a date and time \u2014 the listing publishes now but goes live then."; return; }
  const d = new Date(raw);
  if (isNaN(d.getTime())) { note.innerHTML = '<span class="text-amber-300">That date/time is not valid.</span>'; return; }
  if (d.getTime() < Date.now() + 60_000) { note.innerHTML = '<span class="text-amber-300">Must be in the future.</span>'; return; }
  note.innerHTML = `<span class="text-emerald-300">\u2713 Will go live ${escapeHtml(d.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }))} (your local time)</span>`;
}

function eblUseCat(id: string): void {
  const cat = document.getElementById("ebl_cat") as HTMLInputElement | null;
  if (cat) cat.value = id;
}

// Inline-handler bridges
(window as any).openEbayListingModal = openEbayListingModal;
(window as any)._eblPublish   = eblPublish;
(window as any)._eblBootstrap = eblBootstrap;
(window as any)._eblUseCat    = eblUseCat;
(window as any)._eblFormatChange = eblFormatChange;
(window as any)._eblStartChange  = eblStartChange;
(window as any)._eblStartAtNote  = eblStartAtNote;
